import { BaseService } from "../../core/baseService";
import { ApiResult } from "../../core/types";
import {
  ReservationServiceInterface,
  GetReservationsParams,
  ReservationListPage,
  ReservationDto,
  CreateReservationDto,
  UpdateReservationDto,
  ReservationStatus,
  ReservationInviteResultDto,
  ResolveOverlapRequest,
  CheckoutValidationResultDto,
} from "../types/reservation.types";
import {
  FrontDeskBoardDto,
  QuickWalkInDto,
  WalkInWithResolutionDto,
  StayActivityDto,
} from "../types/frontDesk.types";

/**
 * Service for unified reservation management.
 * Handles both Manual and PMS-sourced reservations through the unified Stay entity.
 */
export class ReservationService
  extends BaseService
  implements ReservationServiceInterface
{
  private readonly basePath = "/api/admin/unified-reservations";

  /**
   * Get paginated list of reservations with filters
   */
  async listReservations(
    params: GetReservationsParams
  ): Promise<ApiResult<ReservationListPage>> {
    const queryParams: Record<string, unknown> = {
      page: params.page || 1,
      pageSize: params.pageSize || 25,
    };

    if (params.dateFrom) {
      queryParams.dateFrom = params.dateFrom;
    }

    if (params.dateTo) {
      queryParams.dateTo = params.dateTo;
    }

    if (params.status) {
      queryParams.status = params.status;
    }

    if (params.source) {
      queryParams.source = params.source;
    }

    if (params.guest) {
      queryParams.guest = params.guest;
    }

    if (params.room) {
      queryParams.room = params.room;
    }

    if (params.sortBy) {
      queryParams.sortBy = params.sortBy;
    }

    if (params.sortOrder) {
      queryParams.sortOrder = params.sortOrder;
    }

    return this.handleRequest<ReservationListPage>(
      this.client.get(this.basePath, { params: queryParams })
    );
  }

  /**
   * Get a single reservation by ID
   */
  async getReservation(id: string): Promise<ApiResult<ReservationDto>> {
    return this.handleRequest<ReservationDto>(
      this.client.get(`${this.basePath}/${id}`)
    );
  }

  /**
   * Create a new manual reservation
   */
  async createReservation(
    data: CreateReservationDto
  ): Promise<ApiResult<ReservationDto>> {
    return this.handleRequest<ReservationDto>(
      this.client.post(this.basePath, data)
    );
  }

  /**
   * Update an existing reservation
   */
  async updateReservation(
    id: string,
    data: UpdateReservationDto
  ): Promise<ApiResult<ReservationDto>> {
    return this.handleRequest<ReservationDto>(
      this.client.put(`${this.basePath}/${id}`, data)
    );
  }

  /**
   * Delete a reservation (only Upcoming or Cancelled)
   */
  async deleteReservation(id: string): Promise<ApiResult<void>> {
    return this.handleRequest<void>(
      this.client.delete(`${this.basePath}/${id}`)
    );
  }

  /**
   * Update reservation status
   */
  async updateStatus(
    id: string,
    status: ReservationStatus
  ): Promise<ApiResult<ReservationDto>> {
    return this.handleRequest<ReservationDto>(
      this.client.patch(`${this.basePath}/${id}/status`, { status })
    );
  }

  /**
   * Send pre-checkin invitation to guest
   */
  async sendPrecheckinInvite(
    id: string,
    recipientEmail?: string
  ): Promise<ApiResult<ReservationInviteResultDto>> {
    return this.handleRequest<ReservationInviteResultDto>(
      this.client.post(`${this.basePath}/${id}/invite`, { recipientEmail })
    );
  }

  /**
   * Check in a reservation (generates QR token if room assigned)
   * Returns 409 with overlap data if conflicts exist.
   */
  async checkIn(id: string): Promise<ApiResult<ReservationDto>> {
    return this.handleRequest<ReservationDto>(
      this.client.post(`${this.basePath}/${id}/checkin`)
    );
  }

  /**
   * Check in with overlap resolution (after user chooses how to resolve conflict)
   */
  async checkInWithResolution(
    id: string,
    resolution: ResolveOverlapRequest
  ): Promise<ApiResult<ReservationDto>> {
    return this.handleRequest<ReservationDto>(
      this.client.post(
        `${this.basePath}/${id}/checkin-with-resolution`,
        resolution
      )
    );
  }

  /**
   * Check out a reservation
   */
  async checkOut(id: string): Promise<ApiResult<ReservationDto>> {
    return this.handleRequest<ReservationDto>(
      this.client.post(`${this.basePath}/${id}/checkout`)
    );
  }

  /**
   * Force check out (bypasses pending items warning)
   */
  async forceCheckOut(id: string): Promise<ApiResult<ReservationDto>> {
    return this.handleRequest<ReservationDto>(
      this.client.post(`${this.basePath}/${id}/checkout`, { force: true })
    );
  }

  /**
   * Validate checkout - returns pending items if any exist
   */
  async validateCheckout(id: string): Promise<ApiResult<CheckoutValidationResultDto>> {
    return this.handleRequest<CheckoutValidationResultDto>(
      this.client.get(`${this.basePath}/${id}/checkout/validate`)
    );
  }

  /**
   * Get front desk board data for a specific date
   */
  async getFrontDeskBoard(
    date?: string
  ): Promise<ApiResult<FrontDeskBoardDto>> {
    const params = date ? { date } : {};
    return this.handleRequest<FrontDeskBoardDto>(
      this.client.get(`${this.basePath}/front-desk-board`, { params })
    );
  }

  /**
   * Quick walk-in: create + check-in in one step
   */
  async quickWalkIn(data: QuickWalkInDto): Promise<ApiResult<ReservationDto>> {
    return this.handleRequest<ReservationDto>(
      this.client.post(`${this.basePath}/walk-in`, data)
    );
  }

  async quickWalkInWithResolution(
    data: WalkInWithResolutionDto
  ): Promise<ApiResult<ReservationDto>> {
    return this.handleRequest<ReservationDto>(
      this.client.post(`${this.basePath}/walk-in-with-resolution`, data)
    );
  }

  async getStayActivity(stayId: string): Promise<ApiResult<StayActivityDto>> {
    return this.handleRequest<StayActivityDto>(
      this.client.get(`${this.basePath}/${stayId}/activity`)
    );
  }
}
